// Whole-document formatting for a TextBox (PRD — "apply to the whole box while
// selected, not editing"). Pure TipTap-JSON transforms that push a format onto
// EVERY text run (and hard break) / block, overriding any per-run marks — so a
// selected (not edited) textbox takes the chosen size / colour / alignment
// across all its text.
//
// Mirrors the mark/attr conventions the live editor uses
// (TextboxEditorController.applyFormat): valued styles are `textStyle` attrs,
// bold/italic/underline/strike are StarterKit toggle marks, and text alignment
// is a block-node attribute.

import type { TextDocument, TextDocumentNode, TextDocumentMark } from './text-document.type.js';

/** The three shapes a whole-document format can take. */
export type WholeDocFormat =
  | { readonly kind: 'textStyle'; readonly attrs: Readonly<Record<string, string>> }
  | { readonly kind: 'mark'; readonly mark: 'bold' | 'italic' | 'underline' | 'strike' }
  | { readonly kind: 'blockAttr'; readonly key: string; readonly value: string };

/** Node types that carry a `textAlign` (and similar) block attribute. */
const BLOCK_TYPES = new Set(['paragraph', 'heading']);

/**
 * Inline node types that carry marks: text runs AND hard breaks. A break keeps
 * its own `textStyle` (PowerPoint wraps `<br>` in a sized span), and a break left
 * at an old size shifts the layout although every run shows the new one (Anke,
 * 2026-10-02) — the live editor's `addMark` covers breaks too.
 */
const MARKED_INLINE_TYPES = new Set(['text', 'hardBreak']);

/** Set/override the given `textStyle` attrs on every text run and hard break. */
function setTextStyle(node: TextDocumentNode, attrs: Readonly<Record<string, string>>): TextDocumentNode {
  if (MARKED_INLINE_TYPES.has(node.type)) {
    const marks: TextDocumentMark[] = [...(node.marks ?? [])];
    const idx = marks.findIndex((m) => m.type === 'textStyle');
    if (idx >= 0) {
      marks[idx] = { type: 'textStyle', attrs: { ...(marks[idx]?.attrs ?? {}), ...attrs } };
    } else {
      marks.push({ type: 'textStyle', attrs: { ...attrs } });
    }
    return { ...node, marks };
  }
  if (node.content) return { ...node, content: node.content.map((c) => setTextStyle(c, attrs)) };
  return node;
}

/** Add a StarterKit toggle mark to every text run and hard break (idempotent — never duplicates). */
function addMark(node: TextDocumentNode, mark: string): TextDocumentNode {
  if (MARKED_INLINE_TYPES.has(node.type)) {
    const marks: TextDocumentMark[] = [...(node.marks ?? [])];
    if (!marks.some((m) => m.type === mark)) marks.push({ type: mark });
    return { ...node, marks };
  }
  if (node.content) return { ...node, content: node.content.map((c) => addMark(c, mark)) };
  return node;
}

/** Set a block-level attr (e.g. `textAlign`) on every paragraph/heading node. */
function setBlockAttr(node: TextDocumentNode, key: string, value: string): TextDocumentNode {
  let out = node;
  if (BLOCK_TYPES.has(node.type)) {
    out = { ...node, attrs: { ...(node.attrs ?? {}), [key]: value } };
  }
  if (out.content) out = { ...out, content: out.content.map((c) => setBlockAttr(c, key, value)) };
  return out;
}

/**
 * Return a new document with `fmt` applied to the WHOLE document (every run /
 * every block). The input is never mutated.
 */
export function applyWholeDocumentFormat(doc: TextDocument, fmt: WholeDocFormat): TextDocument {
  switch (fmt.kind) {
    case 'textStyle':
      return setTextStyle(doc, fmt.attrs) as TextDocument;
    case 'mark':
      return addMark(doc, fmt.mark) as TextDocument;
    case 'blockAttr':
      return setBlockAttr(doc, fmt.key, fmt.value) as TextDocument;
  }
}
